// Placeholder.jsx
import React, { useState } from "react";
import "../../styles/colorSystem.css";

import icon from "../icons/System/cancel.svg";
import deleteIcon from "../icons/System/delete.svg";
import warningIcon from "../icons/warning/info_red.svg";

function ClearIcon({ onClick }) {
  return (
    <button type="button" onClick={onClick}>
      <img src={icon} alt="cancel" className="w-[24px] h-[24px]" />
    </button>
  );
}

export default function Placeholder({
  label = "타이틀을 입력해주세요",
  placeholder = "텍스트를 입력해주세요",
  maxLength = 20,
  value: controlled,
  onChange,         // ✅ React 일반 onChange(e) 핸들러 (e.target.value 사용)
  onValueChange,    // ✅ 옵션: value만 받고 싶은 곳에서 쓰는 콜백 (value: string)
  size = "default", // "default" | "large"
  showOverflowMessage = false,
  overflowMessage,
}) {
  const [innerValue, setInnerValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [overflow, setOverflow] = useState(false);

  // null/undefined 방어
  const rawValue =
    controlled !== undefined && controlled !== null ? controlled : innerValue;
  const value = typeof rawValue === "string" ? rawValue : String(rawValue ?? "");
  const length = value.length;
  const isLarge = size === "large";

  const state =
    !focused && !value
      ? "default"
      : focused && !value
      ? "before"
      : focused && value
      ? "while"
      : !focused && value
      ? "after"
      : "default";

  // small일 때만 내부 X (입력 중일 때만)
  const showClear = !isLarge && length > 0 && state === "while";

  const borderClass = overflow
    ? "border-border-danger"
    : state === "before" || state === "while"
    ? "border-border-primary"
    : "border-border-default";

  const inputTextClass =
    state === "default" || state === "before"
      ? "text-text-disabled"
      : "text-text-default";

  /**
   * 🔹 공통 값 적용 로직
   *  - 내부 state 업데이트
   *  - onValueChange(value) 콜백 (문자열만 필요할 때)
   *  - onChange(event) 콜백 (기존 e.target.value 사용하는 핸들러와 호환)
   */
  const applyValue = (next, isOverflow = false, originalEvent) => {
    // 내부 상태
    if (controlled === undefined) {
      setInnerValue(next);
    }

    // 문자열만 받고 싶은 쪽
    if (onValueChange) {
      onValueChange(next);
    }

    // e.target.value 기대하는 기존 onChange 핸들러와 호환
    if (onChange) {
      if (originalEvent && originalEvent.target) {
        // 원래 이벤트를 복사하면서 value만 next로 교체
        const syntheticEvent = {
          ...originalEvent,
          target: {
            ...originalEvent.target,
            value: next,
          },
        };
        onChange(syntheticEvent);
      } else {
        // clear 등에서 이벤트 객체가 없을 때 최소 형태의 synthetic event 생성
        onChange({
          target: {
            value: next,
          },
        });
      }
    }

    setOverflow(isOverflow);
  };

  const handleChange = (e) => {
    const raw = e.target.value ?? "";
    const isOverflow = raw.length > maxLength;
    const next = raw.slice(0, maxLength);

    applyValue(next, isOverflow, e);
  };

  const handleClear = () => {
    applyValue("", false, null);
  };

  const InputComponent = isLarge ? "textarea" : "input";

  const shouldShowOverflowMessage =
    isLarge && showOverflowMessage && overflow;

  return (
    <div className="flex flex-col gap-[8px]">
      {/* 라벨 */}
      <div className="text-text-subtler text-c2">{label}</div>

      {/* 인풋 영역 */}
      <div
        className={`flex w-full rounded-[12px] bg-white border ${borderClass}
        px-[12px] py-[12px]
        ${isLarge ? "h-[328px]" : "items-center"}`}
      >
        <InputComponent
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className={`flex-1 bg-transparent outline-none text-b1 ${inputTextClass}
          ${isLarge ? "resize-none h-full leading-relaxed" : ""}`}
        />
        {/* small 전용 X 아이콘 */}
        {showClear && <ClearIcon onClick={handleClear} />}
      </div>

      {/* 하단 영역 */}
      <div
        className={`
          flex items-center w-full
          ${isLarge ? "justify-between" : "justify-end"}
        `}
      >
        {isLarge &&
          (overflow ? (
            <span className="flex items-center gap-[4px] text-c2 text-text-danger">
              <img src={warningIcon} alt="경고" />
              <span>{maxLength}자 이내로 입력해주세요</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-[4px] text-c2 text-text-danger"
            >
              <img src={deleteIcon} alt="delete" />
              <span>전체 삭제</span>
            </button>
          ))}

        <div className="text-c2 text-text-subtler">
          {length} / {maxLength}자 이내
        </div>
      </div>

      {shouldShowOverflowMessage && (
        <div className="mt-[4px] flex items-center gap-[4px] text-c2 text-text-danger">
          <img
            src={warningIcon}
            alt="warning"
            className="w-[16px] h-[16px]"
          />
          <span>
            {overflowMessage ?? `※ ${maxLength}자 이내로 입력해주세요`}
          </span>
        </div>
      )}
    </div>
  );
}
